import Card from "@/components/Card"
import InputError from "@/components/InputError"
import SkeletonRow from "@/components/SkeletonRow"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import api from "@/lib/axios"
import { formatDateIndonesia, getInitials } from "@/lib/utils"
import { DataSiswa } from "@/types"
import { AxiosError } from "axios"
import { Loader2 } from "lucide-react"
import { useState } from "react"
import toast from "react-hot-toast"
import { FaCheck, FaFaceSadTear, FaFaceSmileBeam } from "react-icons/fa6"
import { GiBackwardTime } from "react-icons/gi"
import { MdBlockFlipped } from "react-icons/md"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useSearchParams } from "react-router-dom"

type DataCekButawarna = {
  is_cek_butawarna: boolean
  id_data_pendaftaran: string
  nomor_pendaftaran: string
  siswa: DataSiswa
  jalur_jenis: string
  pilihan_prodi: string
  status_buta_warna: "Y" | "T" | null
  golongan_darah: string
  status_loket_2?: number | boolean
  keterangan_loket_2?: string
}

type ValidasiState = {
  id_data_pendaftaran?: string
  status_buta_warna: string
  golongan_darah: string
  keterangan_loket_2: string
  status_loket_2?: number | boolean
}

const getDataCekbutawarna = async (searchQuerySiswa: string) => {
  const response = await api.get(
    `/verifikator/daftar-kembali/loket-2/data-cek-butawarna?id=${searchQuerySiswa}`
  )
  return response.data
}

const patchDataValidasi = async (form: ValidasiState) => {
  const response = await api.patch(
    "/verifikator/daftar-kembali/loket-2/validasi",
    form
  )
  return response.data
}

const patchDataResetValidasi = async (id_data_pendaftaran: string) => {
  const response = await api.patch(
    "/verifikator/daftar-kembali/reset?loket=2",
    { id_data_pendaftaran }
  )
  return response.data
}

export default function Loket2({ disabled }: { disabled: boolean }) {
  const [searchParams] = useSearchParams()
  const idParams = searchParams.get("id")

  const [form, setForm] = useState({
    status_buta_warna: "",
    golongan_darah: "",
    keterangan_loket_2: "",
  })

  const {
    data: dataCekButawarna,
    isPending,
    error,
  } = useQuery<{ data: DataCekButawarna }, AxiosError>({
    queryKey: ["dataCekButawarna", idParams],
    queryFn: () => getDataCekbutawarna(idParams || ""),
    enabled: !!idParams,
  })
  const data: DataCekButawarna | undefined = dataCekButawarna?.data

  const queryClient = useQueryClient()
  const {
    mutate,
    isPending: isPendingValidation,
    error: errorValidation,
  } = useMutation({
    mutationFn: patchDataValidasi,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["dataCekButawarna", idParams],
      })
      queryClient.invalidateQueries({
        queryKey: ["dataStatusLoket", idParams],
      })
      toast.success(data?.message || "Success.")
    },
    onError: (
      error: AxiosError<{ message?: string; errors?: Record<string, string[]> }>
    ) => {
      const err = error as AxiosError<{
        message?: string
        errors?: Record<string, string[]>
      }>
      const errorMessage =
        err.response?.data?.message || "An unexpected error occurred."
      toast.error(errorMessage)
    },
  })

  const {
    mutate: mutateResetValidation,
    isPending: isPendingResetValidation,
    // error: errorResetValidation,
  } = useMutation({
    mutationFn: patchDataResetValidasi,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["dataCekButawarna", idParams],
      })
      queryClient.invalidateQueries({
        queryKey: ["dataStatusLoket", idParams],
      })
      toast.success(data?.message || "Success.")
    },
    onError: (
      error: AxiosError<{ message?: string; errors?: Record<string, string[]> }>
    ) => {
      const err = error as AxiosError<{
        message?: string
        errors?: Record<string, string[]>
      }>
      const errorMessage =
        err.response?.data?.message || "An unexpected error occurred."
      toast.error(errorMessage)
    },
  })

  const handleValidasi = (status: number) => {
    mutate({
      ...form,
      id_data_pendaftaran: data?.id_data_pendaftaran,
      status_loket_2: status,
    })
  }

  const handleReset = () => {
    mutateResetValidation(data?.id_data_pendaftaran || "")
  }

  if (isPending) {
    return (
      <div className="flex flex-col gap-y-3">
        {Array(10)
          .fill(null)
          .map((_, index) => {
            return <SkeletonRow key={index} />
          })}
      </div>
    )
  }

  if (error?.response?.status === 404) {
    return (
      <div>
        Data <span className="text-primary">{idParams}</span> tidak ditemukan.
      </div>
    )
  }

  if (!data) {
    return (
      <div className="flex justify-center items-center h-[50vh]">
        <div>
          <FaFaceSmileBeam className="mx-auto" size={40} />
          <h1 className="mt-3 text-xl font-semibold">
            Lakukan pencarian menggunakan nomor pendaftaran atau nama siswa.
          </h1>
        </div>
      </div>
    )
  }

  if (disabled) {
    return (
      <div className="flex justify-center items-center h-[50vh]">
        <div>
          <FaFaceSadTear className="mx-auto" size={40} />
          <h1 className="mt-3 text-xl font-semibold text-center">
            Siswa atas nama {data?.siswa?.nama_lengkap}{" "}
            <span className="text-primary">
              belum divalidasi di loket sebelumnya
            </span>
          </h1>
        </div>
      </div>
    )
  }

  if (!data?.is_cek_butawarna) {
    return (
      <div className="flex justify-center items-center h-[50vh]">
        <div>
          <FaFaceSmileBeam className="mx-auto" size={40} />
          <h1 className="mt-3 text-xl font-semibold text-center">
            Program studi {data?.pilihan_prodi} pilihan siswa atas nama{" "}
            {data?.siswa?.nama_lengkap}{" "}
            <span className="text-primary">tidak memerlukan tes butawarna</span>
          </h1>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 flex gap-3">
      <Avatar className="w-16 h-16">
        <AvatarImage src={data?.siswa?.path_foto || ""} alt={"foto"} />
        <AvatarFallback>
          {getInitials(data?.siswa?.nama_lengkap || "")}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 overflow-auto">
        <table className="table-auto w-full border-collapse border rounded-xl overflow-hidden">
          <tbody>
            <tr>
              <th className="py-2 px-4 border w-[45%] font-semibold text-start">
                Nomor Pendaftaran
              </th>
              <td className="py-2 px-4 border w-[5%]">:</td>
              <td className="py-2 px-4 border w-[50%]">
                {data?.nomor_pendaftaran}
              </td>
            </tr>
            <tr>
              <th className="py-2 px-4 border w-[45%] font-semibold text-start">
                Nama Peserta
              </th>
              <td className="py-2 px-4 border w-[5%]">:</td>
              <td className="py-2 px-4 border w-[50%]">
                {data?.siswa?.nama_lengkap}
              </td>
            </tr>
            <tr>
              <th className="py-2 px-4 border w-[45%] font-semibold text-start">
                Program Studi
              </th>
              <td className="py-2 px-4 border w-[5%]">:</td>
              <td className="py-2 px-4 border w-[50%]">
                {data?.pilihan_prodi}
              </td>
            </tr>
            <tr>
              <th className="py-2 px-4 border w-[45%] font-semibold text-start">
                Jenis Kelamin
              </th>
              <td className="py-2 px-4 border w-[5%]">:</td>
              <td className="py-2 px-4 border w-[50%]">
                {data?.siswa?.jenis_kelamin === "P"
                  ? "Perempuan"
                  : data?.siswa?.jenis_kelamin === "L"
                  ? "Laki-Laki"
                  : "Bencong"}
              </td>
            </tr>
            <tr>
              <th className="py-2 px-4 border w-[45%] font-semibold text-start">
                Tanggal Lahir
              </th>
              <td className="py-2 px-4 border w-[5%]">:</td>
              <td className="py-2 px-4 border w-[50%]">
                {data?.siswa?.tanggal_lahir &&
                  formatDateIndonesia(data?.siswa?.tanggal_lahir, "medium")}
              </td>
            </tr>
            <tr>
              <th className="py-2 px-4 border w-[45%] font-semibold text-start">
                Berat
              </th>
              <td className="py-2 px-4 border w-[5%]">:</td>
              <td className="py-2 px-4 border w-[50%]">
                {data?.siswa?.berat_badan} kg
              </td>
            </tr>

            <tr>
              <th className="py-2 px-4 border w-[45%] font-semibold text-start">
                Golongan Darah
              </th>
              <td className="py-2 px-4 border w-[5%]">:</td>
              <td className="py-2 px-4 border w-[50%]">
                {data?.golongan_darah}
              </td>
            </tr>
            <tr>
              <th className="py-2 px-4 border w-[45%] font-semibold text-start">
                Status Butawarna
              </th>
              <td className="py-2 px-4 border w-[5%]">:</td>
              <td className="py-2 px-4 border w-[50%]">
                {data?.status_buta_warna === null ? (
                  ""
                ) : data?.status_buta_warna === "Y" ? (
                  <Badge variant={"outline"}>Buta Warna</Badge>
                ) : (
                  <Badge variant={"dark"}> Tidak Buta Warna</Badge>
                )}
              </td>
            </tr>

            <tr>
              <th className="py-2 px-4 border w-[45%] font-semibold text-start">
                Status Validasi Loket
              </th>
              <td className="py-2 px-4 border w-[5%]">:</td>
              <td className="py-2 px-4 border w-[50%]">
                {data?.status_loket_2 === 1 ? (
                  <Badge variant={"dark"}>Sudah Valid</Badge>
                ) : data?.status_loket_2 === 0 ? (
                  <Badge variant={"outline"}>Tidak Valid</Badge>
                ) : (
                  <Badge variant={"secondary"}>Belum Valid</Badge>
                )}
              </td>
            </tr>
            <tr>
              <th className="py-2 px-4 border w-[45%] font-semibold text-start">
                Catatan
              </th>
              <td className="py-2 px-4 border w-[5%]">:</td>
              <td className="py-2 px-4 border w-[50%]">
                {data?.keterangan_loket_2}
              </td>
            </tr>
          </tbody>
        </table>
        {data?.status_loket_2 === null ? (
          <Card className="mt-5">
            <form className="flex flex-col gap-y-5">
              <div>
                <Label>Checklist Status Buta Warna</Label>
                <div className="mt-3 flex gap-5">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={form.status_buta_warna === "T"}
                      onCheckedChange={() =>
                        setForm((prev) => ({
                          ...prev,
                          status_buta_warna:
                            prev.status_buta_warna === "T" ? "" : "T",
                        }))
                      }
                    />
                    <Label>Tidak Buta Warna</Label>
                  </div>
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={form.status_buta_warna === "Y"}
                      onCheckedChange={() =>
                        setForm((prev) => ({
                          ...prev,
                          status_buta_warna:
                            prev.status_buta_warna === "Y" ? "" : "Y",
                        }))
                      }
                    />
                    <Label>Buta Warna</Label>
                  </div>
                </div>
                <InputError
                  text={errorValidation?.response?.data.errors?.status_buta_warna?.join(
                    ","
                  )}
                />
              </div>
              <div>
                <Label>Golongan Darah</Label>
                <div className="mt-3 flex gap-5">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={form.golongan_darah === "A"}
                      onCheckedChange={() =>
                        setForm((prev) => ({
                          ...prev,
                          golongan_darah:
                            prev.golongan_darah === "A" ? "" : "A",
                        }))
                      }
                    />
                    <Label>A</Label>
                  </div>
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={form.golongan_darah === "B"}
                      onCheckedChange={() =>
                        setForm((prev) => ({
                          ...prev,
                          golongan_darah:
                            prev.golongan_darah === "B" ? "" : "B",
                        }))
                      }
                    />
                    <Label>B</Label>
                  </div>
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={form.golongan_darah === "AB"}
                      onCheckedChange={() =>
                        setForm((prev) => ({
                          ...prev,
                          golongan_darah:
                            prev.golongan_darah === "AB" ? "" : "AB",
                        }))
                      }
                    />
                    <Label>AB</Label>
                  </div>
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={form.golongan_darah === "O"}
                      onCheckedChange={() =>
                        setForm((prev) => ({
                          ...prev,
                          golongan_darah:
                            prev.golongan_darah === "O" ? "" : "O",
                        }))
                      }
                    />
                    <Label>O</Label>
                  </div>
                </div>
                <InputError
                  text={errorValidation?.response?.data.errors?.golongan_darah?.join(
                    ","
                  )}
                />
              </div>
              <div className="mt-3">
                <div>
                  <Label>Input Catatan</Label>
                  <Textarea
                    placeholder="Ketik catatan..."
                    className="mt-1"
                    value={form.keterangan_loket_2}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        keterangan_loket_2: event.target.value,
                      }))
                    }
                  />
                  <InputError
                    text={errorValidation?.response?.data.errors?.keterangan_loket_2?.join(
                      ","
                    )}
                  />
                </div>
                <div className="mt-5 flex justify-end gap-3">
                  {isPendingValidation ? (
                    <Button
                      variant={"outline"}
                      className="flex items-center gap-3"
                      disabled
                    >
                      <>
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        Processing...
                      </>
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant={"outline"}
                        className="flex items-center gap-3"
                        onClick={() => handleValidasi(0)}
                        disabled={isPendingValidation}
                      >
                        <MdBlockFlipped />
                        Tidak Valid
                      </Button>
                      <Button
                        className="flex items-center gap-3"
                        onClick={() => handleValidasi(1)}
                        disabled={isPendingValidation}
                      >
                        <FaCheck />
                        Valid
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </form>
          </Card>
        ) : (
          <div className="mt-5 flex justify-end">
            <Button
              variant="outline"
              className="flex items-center gap-3"
              onClick={() => handleReset()}
              disabled={isPendingResetValidation}
            >
              {isPendingResetValidation ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <GiBackwardTime />
                  Reset Hasil Validasi
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
